import React, { useRef, useEffect, useMemo } from "react";
import Icon from "../../../components/AppIcon";

const MicConsole = ({
  micState,
  startMicrophone,
  stopMicrophone,
  selectMicDevice,
  setMicInputMode,
  setMicVolume,
  setMicEqLow,
  setMicEqMid,
  setMicEqHigh,
  applyVoiceFx,
  setMicEchoDelay,
  setMicEchoRepeats,
  setAutoDucking,
  duckingFactor,
  setDuckingFactor,
  availableMics,
  color = "primary"
}) => {
  const oscCanvasRef = useRef(null);

  // Constants for theme colors
  const activeColorMap = {
    primary: "border-primary bg-primary/10 text-primary shadow-terminal-glow",
    cyan: "border-terminal-cyan bg-terminal-cyan/10 text-terminal-cyan shadow-[0_0_12px_rgba(0,255,204,0.3)]",
    blue: "border-terminal-blue bg-terminal-blue/10 text-terminal-blue shadow-[0_0_12px_rgba(59,130,246,0.3)]",
    warning: "border-warning bg-warning/10 text-warning shadow-[0_0_12px_rgba(245,158,11,0.3)]"
  };

  const themeColors = useMemo(() => {
    const maps = {
      primary: { line: "#00ff41", bg: "rgba(0, 255, 65, 0.05)" },
      cyan: { line: "#00ffcc", bg: "rgba(0, 255, 204, 0.05)" },
      blue: { line: "#3b82f6", bg: "rgba(59, 130, 246, 0.05)" },
      warning: { line: "#f59e0b", bg: "rgba(245, 158, 11, 0.05)" }
    };
    return maps[color] || maps.primary;
  }, [color]);

  // Live Oscilloscope Voice Visualizer Loop (Local canvas redraw at 60fps)
  useEffect(() => {
    let animId;
    const canvas = oscCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    // Re-measure every frame instead of once on mount. A single measurement taken while
    // the panel is mid-layout (tab fade-in, echo customizer opening, window resize)
    // returns 0 and leaves the scope permanently blank with nothing to trigger a redraw.
    let w = 0;
    let h = 0;

    const syncCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      if (cw === 0 || ch === 0) return false;
      if (cw !== w || ch !== h || canvas.width !== Math.round(cw * dpr)) {
        w = cw;
        h = ch;
        canvas.width = Math.round(cw * dpr);
        canvas.height = Math.round(ch * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      return true;
    };

    const drawWave = () => {
      if (!syncCanvasSize()) {
        // Not laid out yet — keep polling until it has a size
        animId = requestAnimationFrame(drawWave);
        return;
      }

      ctx.fillStyle = "#0c0d12";
      ctx.fillRect(0, 0, w, h);

      let dataArray = null;
      let bufferLength = 0;

      if (window.djEngine && window.djEngine.isMicActive && window.djEngine.micAnalyserNode) {
        const analyser = window.djEngine.micAnalyserNode;
        bufferLength = analyser.frequencyBinCount;
        dataArray = new Uint8Array(bufferLength);
        analyser.getByteTimeDomainData(dataArray);
      }

      ctx.lineWidth = 1.5;
      ctx.strokeStyle = themeColors.line;
      ctx.beginPath();

      if (dataArray && bufferLength > 0) {
        const sliceWidth = w / bufferLength;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0; // 0.0 to 2.0
          const y = (v * h) / 2;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
          x += sliceWidth;
        }
      } else {
        // Draw flat line when microphone is inactive
        ctx.moveTo(0, h / 2);
        ctx.lineTo(w, h / 2);
      }

      ctx.stroke();
      animId = requestAnimationFrame(drawWave);
    };

    drawWave();
    return () => cancelAnimationFrame(animId);
  }, [micState.isActive, themeColors]);

  const handleMicToggle = () => {
    if (micState.isActive) {
      stopMicrophone();
    } else {
      startMicrophone(micState.selectedDeviceId || null);
    }
  };

  const handleMicDeviceChange = (e) => {
    const deviceId = e.target.value;
    selectMicDevice(deviceId);
    if (micState.isActive) {
      startMicrophone(deviceId);
    }
  };

  const inputMode = micState.inputMode || "raw";

  const inputModes = [
    {
      id: "raw",
      label: "Studio Raw",
      hint: "Headphones"
    },
    {
      id: "speaker",
      label: "Speaker Safe",
      hint: "Echo cancel"
    }
  ];

  const fxPresets = [
    { type: "clean", name: "Clean Vocal", desc: "Studio clean input" },
    { type: "echo", name: "DJ Vocal Echo", desc: "Smooth feedback trails" },
    { type: "reverb", name: "Big Reverb", desc: "Stadium convolution spacing" },
    { type: "megaphone", name: "Megaphone", desc: "Megaphone projection" },
    { type: "radio", name: "Radio Preset", desc: "Bandpass distortion" },
    { type: "robot", name: "Cylon Robot", desc: "55Hz ring modulation" },
    { type: "deep", name: "Deep Pitch", desc: "Modulated octave drop" },
    { type: "chipmunk", name: "Chipmunk", desc: "High octave sweep" }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-3.5 sm:p-4 h-full flex flex-col justify-between terminal-glow font-source">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5 border-b border-border pb-2 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="p-1 bg-primary/10 border border-primary/20 rounded">
            <Icon name="Mic" size={13} className="text-primary" />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            Voice Microphone FX Console
          </h3>
        </div>

        <div className="flex items-center space-x-2 w-full sm:w-auto justify-between sm:justify-end">
          {/* Hardware Device Picker */}
          {availableMics && availableMics.length > 0 && (
            <select
              value={micState.selectedDeviceId || ""}
              onChange={handleMicDeviceChange}
              className="bg-zinc-950 border border-zinc-800 text-[9px] text-muted-foreground rounded px-1.5 py-0.5 outline-none max-w-[140px] truncate"
            >
              <option value="">Default Mic</option>
              {availableMics.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.substring(0, 5)}`}
                </option>
              ))}
            </select>
          )}

          <span className={`text-[8.5px] font-source uppercase font-bold px-2 py-0.5 rounded border ${
            micState.health === "recovering"
              ? 'bg-amber-950/80 text-amber-400 border-amber-800/60 animate-pulse'
              : micState.health === "lost"
                ? 'bg-red-950/80 text-red-400 border-red-800/60'
                : micState.isActive
                  ? 'bg-emerald-950/80 text-emerald-400 border-emerald-800/60 animate-pulse'
                  : 'bg-zinc-900 text-zinc-500 border-zinc-800'
          }`}>
            {micState.health === "recovering"
              ? "◐ Reconnecting Input"
              : micState.health === "lost"
                ? "✕ Input Lost"
                : micState.isActive ? "● Live Input Stream Active" : "○ Input Suspended"}
          </span>
        </div>
      </div>

      {/* min-h-0 + scrollable: never clip content away with no way to reach it. The old
          lg:overflow-hidden silently sheared off the EQ sliders and the waveform scope
          as soon as either column grew taller than the fixed console height. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-grow min-h-0 mt-3 items-stretch overflow-y-auto pr-0.5">
        {/* Col 1: Master controls, Ducking, EQs */}
        <div className="col-span-1 lg:col-span-5 flex flex-col justify-between pr-0 lg:pr-2 border-b lg:border-b-0 lg:border-r border-zinc-800/40 pb-4 lg:pb-0 space-y-3">
          
          {/* Master Toggle & Volume */}
          <div className="space-y-2.5">
            <button
              onClick={handleMicToggle}
              className={`w-full py-2.5 rounded-lg border font-source font-bold text-xs uppercase tracking-wider transition-all duration-200 flex items-center justify-center space-x-2 ${
                micState.isActive 
                  ? "bg-emerald-600 text-white border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.4)] hover:brightness-105" 
                  : "bg-zinc-900 border-zinc-800 text-muted-foreground hover:text-foreground hover:bg-zinc-800"
              }`}
            >
              <Icon name="Power" size={13} className={micState.isActive ? "animate-pulse" : ""} />
              <span>{micState.isActive ? "Disable Vocal Input" : "Enable Vocal Input"}</span>
            </button>

            {/* Input health readout — never leave a dead input looking like a flat line */}
            {micState.healthDetail && micState.health !== "live" && (
              <div className={`flex items-start justify-between gap-2 text-[8.5px] font-source px-2 py-1.5 rounded border ${
                micState.health === "lost"
                  ? "bg-red-950/40 text-red-300 border-red-900/60"
                  : "bg-amber-950/40 text-amber-300 border-amber-900/60"
              }`}>
                <span className="leading-tight">{micState.healthDetail}</span>
                {micState.health === "lost" && (
                  <button
                    onClick={() => startMicrophone(micState.selectedDeviceId || null)}
                    className="flex-shrink-0 uppercase font-bold underline hover:text-white"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}

            {/* Mic Fader */}
            <div className="space-y-1 bg-black/30 p-2 rounded-lg border border-zinc-900">
              <div className="flex justify-between items-center text-[9px] font-source">
                <span className="text-muted-foreground uppercase font-bold">Microphone Volume</span>
                <span className="text-primary font-mono font-bold text-[10px]">{Math.round(micState.volume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1.0"
                step="0.01"
                value={micState.volume}
                onChange={(e) => setMicVolume(parseFloat(e.target.value))}
                className="w-full h-1.5 cursor-pointer accent-primary bg-zinc-850 rounded"
              />
            </div>
          </div>

          {/* Input Capture Mode — compact single row so the console still fits its height */}
          <div className="flex items-center justify-between gap-2 bg-black/30 px-2 py-1.5 rounded-lg border border-zinc-900">
            <span
              className="text-[9px] font-source text-muted-foreground uppercase font-bold leading-tight"
              title={inputMode === "raw"
                ? "Raw capture, no browser echo cancellation: full sentences and full bandwidth. Use headphones — an open mic on laptop speakers can howl."
                : "Browser echo cancellation and noise suppression on, so laptop speakers do not feed back. May clip syllables on your own voice."}
            >
              Capture
            </span>

            <div className="flex items-center gap-1">
              {micState.feedbackGuardActive && (
                <span className="text-[7.5px] font-source uppercase font-bold px-1 py-0.5 rounded border bg-amber-950/80 text-amber-400 border-amber-800/60 animate-pulse">
                  ⚠ Guard
                </span>
              )}
              {inputModes.map((mode) => {
                const isActive = inputMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    onClick={() => setMicInputMode && setMicInputMode(mode.id)}
                    title={mode.hint}
                    className={`px-2 py-1 rounded border transition duration-150 cursor-pointer text-[8.5px] font-bold uppercase leading-none font-source ${
                      isActive
                        ? activeColorMap[color]
                        : "border-zinc-800/80 bg-zinc-900/60 hover:bg-zinc-800 hover:border-zinc-700 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auto-Ducking controls */}
          <div className="space-y-2 bg-black/30 p-2.5 rounded-lg border border-zinc-900">
            <div className="flex items-center justify-between text-[9px] font-source">
              <span className="text-muted-foreground uppercase font-bold flex items-center space-x-1">
                <span>Auto-Ducking</span>
              </span>
              <label className="flex items-center space-x-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={micState.autoDucking}
                  onChange={(e) => setAutoDucking(e.target.checked)}
                  className="rounded bg-zinc-950 border-zinc-800 text-primary focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                />
                <span className={`font-bold text-[9px] ${micState.autoDucking ? 'text-primary' : 'text-muted-foreground'}`}>ENABLE</span>
              </label>
            </div>
            
            <p className="text-[8.5px] text-zinc-500 font-source leading-tight">
              Automatically lowers the music volume when you speak, and recovers when you pause.
            </p>

            <div className="space-y-1 pt-1">
              <div className="flex justify-between items-center text-[8.5px] font-source">
                <span className="text-muted-foreground uppercase">Music Ducking Depth</span>
                <span className="text-terminal-cyan font-mono font-bold">{Math.round((1 - duckingFactor) * 100)}% Drop</span>
              </div>
              <input
                type="range"
                min="0.0"
                max="0.8"
                step="0.05"
                disabled={!micState.autoDucking}
                value={1 - duckingFactor}
                onChange={(e) => setDuckingFactor(1 - parseFloat(e.target.value))}
                className="w-full h-1.5 cursor-pointer accent-terminal-cyan bg-zinc-850 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Vocal EQ Equalizer */}
          <div className="bg-black/30 p-2.5 rounded-lg border border-zinc-900 space-y-2">
            <span className="text-[9px] font-source text-muted-foreground uppercase font-bold block">Vocal EQ Equalizer</span>
            <div className="grid grid-cols-3 gap-2 py-1">
              <div className="flex flex-col items-center space-y-1">
                <input
                  type="range"
                  orient="vertical"
                  min="-12"
                  max="12"
                  step="0.5"
                  value={micState.eqHigh}
                  onChange={(e) => setMicEqHigh(parseFloat(e.target.value))}
                  className="h-14 sm:h-16 w-1.5 cursor-pointer accent-primary bg-zinc-800 rounded"
                  style={{ WebkitAppearance: "slider-vertical" }}
                />
                <span className="text-[8px] font-source text-zinc-400 uppercase font-bold">High</span>
                <span className="text-[8.5px] font-mono font-bold text-foreground">{micState.eqHigh > 0 ? `+${micState.eqHigh}` : micState.eqHigh}</span>
              </div>
              <div className="flex flex-col items-center space-y-1">
                <input
                  type="range"
                  orient="vertical"
                  min="-12"
                  max="12"
                  step="0.5"
                  value={micState.eqMid}
                  onChange={(e) => setMicEqMid(parseFloat(e.target.value))}
                  className="h-14 sm:h-16 w-1.5 cursor-pointer accent-primary bg-zinc-800 rounded"
                  style={{ WebkitAppearance: "slider-vertical" }}
                />
                <span className="text-[8px] font-source text-zinc-400 uppercase font-bold">Mid</span>
                <span className="text-[8.5px] font-mono font-bold text-foreground">{micState.eqMid > 0 ? `+${micState.eqMid}` : micState.eqMid}</span>
              </div>
              <div className="flex flex-col items-center space-y-1">
                <input
                  type="range"
                  orient="vertical"
                  min="-12"
                  max="12"
                  step="0.5"
                  value={micState.eqLow}
                  onChange={(e) => setMicEqLow(parseFloat(e.target.value))}
                  className="h-14 sm:h-16 w-1.5 cursor-pointer accent-primary bg-zinc-800 rounded"
                  style={{ WebkitAppearance: "slider-vertical" }}
                />
                <span className="text-[8px] font-source text-zinc-400 uppercase font-bold">Low</span>
                <span className="text-[8.5px] font-mono font-bold text-foreground">{micState.eqLow > 0 ? `+${micState.eqLow}` : micState.eqLow}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Col 2: Voice FX presets grid & visualizer */}
        <div className="col-span-1 lg:col-span-7 flex flex-col justify-between pl-0 lg:pl-1 space-y-3">
          {/* Preset buttons */}
          <div className="space-y-1.5 flex-grow">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-source text-muted-foreground uppercase font-bold">Voice FX Modulation Presets</span>
              <span className="text-[8px] font-mono text-zinc-500">8 DSP FX</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {fxPresets.map((preset) => {
                const isActive = micState.voiceFxType === preset.type;
                return (
                  <button
                    key={preset.type}
                    onClick={() => applyVoiceFx(preset.type)}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-lg border text-center transition duration-150 relative group cursor-pointer ${
                      isActive 
                        ? activeColorMap[color] 
                        : "border-zinc-800/80 bg-zinc-900/60 hover:bg-zinc-800 hover:border-zinc-700 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="text-[9px] font-bold tracking-tight block truncate uppercase leading-none font-source">
                      {preset.name}
                    </span>
                    <span className="text-[7.5px] text-zinc-400 block leading-tight mt-1 opacity-80 group-hover:opacity-100 truncate w-full">
                      {preset.desc}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* DJ Vocal Echo Customizer Controls (Visible when Echo preset is active or clicked) */}
          {micState.voiceFxType === "echo" && (
            <div className="bg-black/40 border border-primary/30 p-2.5 rounded-lg space-y-2 animate-fadeIn shadow-terminal-glow">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-source text-primary font-bold uppercase tracking-wider flex items-center space-x-1">
                  <span>🎤 DJ Vocal Echo Customizer</span>
                </span>
                <span className="text-[8px] font-mono text-emerald-400 font-bold bg-emerald-950/60 border border-emerald-800/60 px-1.5 py-0.5 rounded">
                  Full-Phrase Mode
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-0.5">
                {/* Delay Time Control */}
                <div className="space-y-1 bg-zinc-950 p-2 rounded border border-zinc-900">
                  <div className="flex justify-between items-center text-[8.5px] font-source">
                    <span className="text-zinc-400 uppercase font-bold">Echo Delay Time</span>
                    <span className="text-terminal-cyan font-mono font-bold">{micState.echoDelayTime || 650} ms</span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="1500"
                    step="50"
                    value={micState.echoDelayTime || 650}
                    onChange={(e) => setMicEchoDelay && setMicEchoDelay(parseInt(e.target.value))}
                    className="w-full h-1.5 cursor-pointer accent-terminal-cyan bg-zinc-850 rounded"
                  />
                  <div className="flex justify-between gap-1 pt-0.5">
                    {[
                      { label: "350ms", val: 350 },
                      { label: "650ms (Phrase)", val: 650 },
                      { label: "900ms", val: 900 },
                      { label: "1200ms", val: 1200 }
                    ].map(p => (
                      <button
                        key={p.val}
                        onClick={() => setMicEchoDelay && setMicEchoDelay(p.val)}
                        className={`text-[7.5px] font-mono px-1 py-0.5 rounded border transition-colors ${
                          (micState.echoDelayTime || 650) === p.val 
                            ? "bg-primary text-black font-bold border-primary" 
                            : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Repeats Count Control */}
                <div className="space-y-1 bg-zinc-950 p-2 rounded border border-zinc-900">
                  <div className="flex justify-between items-center text-[8.5px] font-source">
                    <span className="text-zinc-400 uppercase font-bold">Echo Repeats</span>
                    <span className="text-primary font-mono font-bold">{micState.echoRepeats || 3} Repeats</span>
                  </div>
                  <div className="grid grid-cols-6 gap-1 pt-1">
                    {[1, 2, 3, 4, 5, 8].map((count) => {
                      const isActive = (micState.echoRepeats || 3) === count;
                      return (
                        <button
                          key={count}
                          onClick={() => setMicEchoRepeats && setMicEchoRepeats(count)}
                          className={`py-1 rounded text-[8px] font-bold font-mono border transition-all text-center ${
                            isActive
                              ? "bg-primary text-black border-primary shadow-[0_0_8px_rgba(0,255,65,0.4)]"
                              : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800 hover:text-white"
                          }`}
                        >
                          {count}x
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Phrase Repeat Live Simulation Readout */}
              <div className="bg-zinc-950/80 border border-zinc-900 p-1.5 rounded text-[8px] font-source flex items-center justify-between text-zinc-400">
                <span className="font-bold text-zinc-500 uppercase">PROMPTED ECHO REPEAT STREAM:</span>
                <span className="font-mono text-emerald-400 font-bold truncate max-w-[240px]">
                  "Dj Kahnu" {Array.from({ length: micState.echoRepeats || 3 }, () => '➜ "Dj Kahnu"').join(' ')}
                </span>
              </div>
            </div>
          )}

          {/* Live Waveform Oscilloscope Display */}
          <div className="mt-2 flex-shrink-0">
            <span className="text-[9px] font-source text-muted-foreground uppercase font-bold mb-1.5 block">Live Voice Waveform Scope</span>
            <div className="relative h-24 bg-zinc-950 rounded-lg overflow-hidden border border-zinc-900">
              <canvas ref={oscCanvasRef} className="w-full h-full block" />
              
              {/* Mic level bar inside visualizer */}
              {micState.isActive && (
                <div className="absolute bottom-2 left-2 right-2 flex items-center space-x-1.5 bg-black/70 py-0.5 px-2 rounded border border-zinc-900 text-[7.5px] font-source text-zinc-400 leading-none">
                  <span>INPUT LEVEL:</span>
                  <div className="flex-grow bg-zinc-900 h-1.5 rounded-full overflow-hidden border border-zinc-950">
                    <div id="mic-level-bar" className="h-full bg-emerald-500 transition-all duration-75" style={{ width: "0%" }}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MicConsole;
